/**
 * "Save to a space" — name a mental model and choose where it lives: your own
 * space, or one of your team spaces.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { Modal, TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import type { MapDoc, MapLevelNumber } from './model';
import { listSaveSpaces, saveSavedModel, savedModelPath, type SaveSpace } from './savedModels';

export function SaveToSpace({
  open,
  onClose,
  doc,
  defaultName,
  level,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  doc: MapDoc;
  defaultName: string;
  level?: MapLevelNumber | null;
  /** Called with the new model's id. Without it, the dialog shows where the model went. */
  onSaved?: (modelId: string) => void;
}) {
  const [spaces, setSpaces] = useState<SaveSpace[] | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(defaultName);
    setError(null);
    setSavedId(null);
    let cancelled = false;
    void listSaveSpaces().then((result) => {
      if (cancelled) return;
      if (!result.ok || !result.data) {
        setError(result.error ?? 'Your spaces could not be loaded.');
        setSpaces([]);
        return;
      }
      setSpaces(result.data);
      setSpaceId((current) => current ?? result.data!.find((s) => s.canSave)?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [open, defaultName]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !spaceId || busy) return;
    setBusy(true);
    setError(null);
    const result = await saveSavedModel({ spaceId, name: name.trim(), doc, level: level ?? null });
    setBusy(false);
    if (!result.ok || !result.data) {
      setError(result.error ?? 'The model could not be saved.');
      return;
    }
    if (onSaved) onSaved(result.data);
    else setSavedId(result.data);
  };

  const chosen = spaces?.find((s) => s.id === spaceId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      label="Save"
      title="Save this mental model"
      description="Give it a name and choose where it lives. In your own space only you see it; in a team space, everyone in that team does."
    >
      {savedId ? (
        <div className="space-y-3">
          <p className="font-body text-sm text-[var(--text-pri)]">
            Saved as “{name.trim()}” in {chosen?.name ?? 'your space'}.
          </p>
          <div className="flex gap-2">
            <Button variant="cta" onClick={() => window.open(savedModelPath(savedId), '_blank', 'noopener')}>
              Open it
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <label className="block font-body text-sm text-[var(--text-sec)]">
            Name
            <TextInput className="mt-1" value={name} maxLength={120} autoFocus onChange={(e) => setName(e.target.value)} />
          </label>

          <fieldset>
            <legend className="font-body text-sm text-[var(--text-sec)]">Where</legend>
            {spaces === null ? (
              <p className="mt-1 font-body text-sm text-[var(--text-dim)]">Loading your spaces…</p>
            ) : (
              <div className="mt-1 max-h-56 space-y-1 overflow-y-auto">
                {spaces.map((space) => (
                  <label
                    key={space.id}
                    className={`flex items-center gap-3 rounded-md px-2 py-1.5 font-body text-sm ${
                      space.canSave ? 'cursor-pointer text-[var(--text-pri)] hover:bg-[var(--surface)]' : 'text-[var(--text-dim)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="mental-model-space"
                      checked={spaceId === space.id}
                      disabled={!space.canSave}
                      onChange={() => setSpaceId(space.id)}
                    />
                    <span className="min-w-0 flex-1 truncate">{space.name}</span>
                    <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]">
                      {space.kind === 'personal' ? 'Only you' : space.canSave ? 'Team' : 'Team · view only'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {error && <p className="font-body text-sm text-[var(--coral)]">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="cta" disabled={!name.trim() || !spaceId || busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
